// components/Cart/Cart.tsx

import React, { useState, useRef, useEffect } from 'react';
import { Item, CartItemSelection } from '../../types';
import { Button } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../../utils/functions';
import { FaPen, FaPlus, FaMinus, FaCheck, FaXmark, FaChevronDown } from 'react-icons/fa6';
import { HiOutlinePencilSquare, HiOutlineTrash, HiOutlineChatBubbleOvalLeft } from 'react-icons/hi2';
import { createPortal } from 'react-dom';
import { localDB } from '../../../../../../../crud/localDB/localDB';
import './Cart.scss'; // Import the SCSS file

interface CartProps {
  cart: CartItemSelection[];
  pedidoType: "Delivery" | "Caja" | "Restaurant" | "Autoservicio";
  allItems: Item[];
  handleCartQuantityChange: (index: number, newQuantity: number) => void;
  loadCartItemForEditing: (index: number) => void;
  handleDeleteCartItem: (index: number) => void;
  handleCartItemCommentChange?: (index: number, comment: string) => void;
  handleCourseTagChange?: (index: number, tag: 'ENTRADA' | 'FONDO' | undefined) => void;
  handleCartClientIndexChange?: (index: number, clientIndex: number) => void;
  reverseOrder?: boolean; // if true, show last-added items first
}

const Cart: React.FC<CartProps> = ({
  cart,
  pedidoType,
  allItems,
  handleCartQuantityChange,
  loadCartItemForEditing,
  handleDeleteCartItem,
  handleCartItemCommentChange,
  handleCourseTagChange,
  handleCartClientIndexChange,
  reverseOrder,
}) => {
  const [editingComments, setEditingComments] = useState<{ [index: number]: boolean }>({});
  const [commentValues, setCommentValues] = useState<{ [index: number]: string }>({});
  const [expandedItems, setExpandedItems] = useState<{ [index: number]: boolean }>({});
  const [enableDoubleClickCourse, setEnableDoubleClickCourse] = useState(false);
  const [activeClientDropdown, setActiveClientDropdown] = useState<string | null>(null);
  const [clientDropdownPosition, setClientDropdownPosition] = useState<{top: number, left: number, width: number} | null>(null);
  const commentInputRefs = useRef<{ [index: number]: HTMLInputElement | null }>({});
  const commentContainerRefs = useRef<{ [index: number]: HTMLDivElement | null }>({});
  const editingCommentsRef = useRef<{ [index: number]: boolean }>({});
  const commentValuesRef = useRef<{ [index: number]: string }>({});


  const startEditingComment = (index: number, currentComment: string = '') => {
    setEditingComments(prev => ({ ...prev, [index]: true }));
    setCommentValues(prev => ({ ...prev, [index]: currentComment }));
    
    // Focus the input after state update
    setTimeout(() => {
      const input = commentInputRefs.current[index];
      if (input) {
        input.focus();
        input.select();
      }
    }, 50);
  };

  const cancelEditingComment = (index: number) => {
    setEditingComments(prev => ({ ...prev, [index]: false }));
    setCommentValues(prev => ({ ...prev, [index]: '' }));
  };

  const saveComment = (index: number) => {
    const comment = commentValues[index]?.trim() || '';
    if (handleCartItemCommentChange) {
      handleCartItemCommentChange(index, comment);
    }
    setEditingComments(prev => ({ ...prev, [index]: false }));
    setCommentValues(prev => ({ ...prev, [index]: '' }));
  };

  const handleCommentKeyPress = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveComment(index);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditingComment(index);
    }
  };

  // Handle double-click to toggle course tag
  const handleCourseTagToggle = (index: number) => {
    if (!enableDoubleClickCourse || !handleCourseTagChange) return;
    
    const currentTag = cart[index].course_tag;
    let newTag: 'ENTRADA' | 'FONDO' | undefined;
    
    if (!currentTag) {
      newTag = 'ENTRADA';
    } else if (currentTag === 'ENTRADA') {
      newTag = 'FONDO';
    } else {
      newTag = undefined;
    }
    
    // Call the handler to update the cart state in OrderTaker
    handleCourseTagChange(index, newTag);
  };

  // Toggle expand/collapse for cart item details
  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Client selector dropdown handlers
  const handleToggleClientDropdown = (dropdownId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (activeClientDropdown === dropdownId) {
      setActiveClientDropdown(null);
      setClientDropdownPosition(null);
    } else {
      const toggleButton = event.currentTarget as HTMLElement;
      const rect = toggleButton.getBoundingClientRect();
      setClientDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 100)
      });
      setActiveClientDropdown(dropdownId);
    }
  };

  const handleSelectClientForItem = (index: number, clientIndex: number) => {
    if (handleCartClientIndexChange) {
      handleCartClientIndexChange(index, clientIndex);
    }
    setActiveClientDropdown(null);
    setClientDropdownPosition(null);
  };

  const formatClientLabel = (clientIndex: number) => {
    return clientIndex === -1 ? 'Grupo' : `C${clientIndex + 1}`;
  };

  // Close client dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeClientDropdown) {
        const target = event.target as Element;
        const isInsideDropdown = target.closest('.cart-client-selector') || target.closest('.portal-client-dropdown-menu');
        if (!isInsideDropdown) {
          setActiveClientDropdown(null);
          setClientDropdownPosition(null);
        }
      }
    };

    if (activeClientDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeClientDropdown]);

  // Calculate the total value of the entire cart
  const cartTotal = cart.reduce((sum, ci) => sum + ci.total_price, 0);

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const enableDoubleClick = await localDB.get<boolean>('pedidosConfig', 'enableDoubleClickCourse');
        setEnableDoubleClickCourse(enableDoubleClick ?? false);
      } catch (error) {
        console.error('Error loading cart config:', error);
      }
    };
    loadConfig();
  }, []);

  React.useEffect(() => {
  // keep refs updated so unmount cleanup can access latest values
  editingCommentsRef.current = editingComments;
  commentValuesRef.current = commentValues;

  const handleGlobalClick = (e: MouseEvent) => {
    // índices que están en edición
    const openIndexes = Object.keys(editingComments)
      .map(Number)
      .filter(idx => editingComments[idx]);

    if (openIndexes.length === 0) return;

    // Si el clic ocurrió dentro de algún editor abierto, no hacemos nada
    for (const idx of openIndexes) {
      const container = commentContainerRefs.current[idx];
      if (container && container.contains(e.target as Node)) {
        return;
      }
    }

    // Clic fuera -> guardamos el primero que esté abierto
    saveComment(openIndexes[0]);
  };

  document.addEventListener('mousedown', handleGlobalClick);
  return () => document.removeEventListener('mousedown', handleGlobalClick);
}, [editingComments, commentValues]);

  // On unmount (e.g. modal closes and Cart unmounts) flush any open comment editors
  React.useEffect(() => {
    return () => {
      try {
        const openIndexes = Object.keys(editingCommentsRef.current || {})
          .map(Number)
          .filter(idx => editingCommentsRef.current[idx]);

        if (openIndexes.length === 0) return;

        for (const idx of openIndexes) {
          const comment = (commentValuesRef.current[idx] || '').trim();
          if (handleCartItemCommentChange) {
            handleCartItemCommentChange(idx, comment);
          }
        }
      } catch (err) {
        // swallow errors during unmount
        console.error('Error flushing pending comments on unmount', err);
      }
    };
  }, []);

  // Prepare items for display. Keep original indices so handlers map correctly.
  const itemsToDisplay = cart.map((ci, idx) => ({ ci, originalIndex: idx }));
  if (reverseOrder) itemsToDisplay.reverse();

  return (
    <div className="cart-container">
      {(pedidoType === "Autoservicio") &&
        <h2 className="cart-title">Resumen</h2>
      }
      <ul className="cart-list">
        {itemsToDisplay.map((entry, displayIdx) => {
          const ci = entry.ci;
          const index = entry.originalIndex; // map displayed item back to original cart index
          // Validate cart item structure
          if (!ci || !ci.item_id) {
            console.error('Invalid cart item at index:', index);
            return null;
          }
          const item = allItems.find(i => i && i.item_id === ci.item_id);
          
          // Generate comma-separated list for modifiers with defensive checks
          const modifierOptions = Array.isArray(ci.modifiers) && ci.modifiers.length > 0
            ? ci.modifiers
                .filter(mod => mod && mod.option_name)
                .map(mod => mod.option_name)
                .join(', ')
            : '';

          const isEditingComment = editingComments[index];
          const currentComment = commentValues[index] || ci.comment || '';
          const displayName = item?.item_name || ci.item_name || 'Item desconocido';
          const displayPrice = typeof ci.total_price === 'number' ? ci.total_price : 0;

          return (
            <div key={ci.selection_id || index} className={`cart-item ${isEditingComment ? 'editing-comment' : ''}`}>
              {/* Main Cart Item Row */}
              <div className="cart-item-main-row">
                {/* Quantity Controls - Different for granel items */}
                {ci.is_granel ? (
                  <div className='granel-quantity-display'>
                    <span className="granel-quantity-text">
                      {ci.quantity} {ci.unit_acronym}
                    </span>
                    <Button
                      variant="none"
                      className="edit-granel-button"
                      onClick={() => loadCartItemForEditing(index)}
                      aria-label="Edit granel quantity"
                    >
                      <FaPen />
                    </Button>
                  </div>
                ) : (
                  <div className='item-quantity-selector'>
                    <Button
                      variant="none"
                      className="quantity-button-cart decrement"
                      onClick={() => handleCartQuantityChange(index, ci.quantity - 1)}
                      disabled={ci.quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <FaMinus />
                    </Button>
                    <span className="cart-item-quantity" aria-label={`Quantity: ${ci.quantity}`}>
                      {ci.quantity}
                    </span>
                    <Button
                      variant="none"
                      className="quantity-button-cart increment"
                      onClick={() => handleCartQuantityChange(index, ci.quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <FaPlus />
                    </Button>
                  </div>
                )}

                {/* Client Selector - Only show for Restaurant pedido type */}
                {pedidoType === 'Restaurant' && handleCartClientIndexChange && (
                  <div className="cart-client-selector">
                    <button
                      className={`cart-client-toggle ${activeClientDropdown === `client-${index}` ? 'active' : ''}`}
                      onClick={(e) => handleToggleClientDropdown(`client-${index}`, e)}
                      aria-label="Select client"
                    >
                      {formatClientLabel(ci.clientIndex)}
                      <FaChevronDown className="dropdown-arrow" />
                    </button>
                  </div>
                )}

                {/* Item Information */}
                <div className='item-information'>
                  <div 
                    onDoubleClick={() => enableDoubleClickCourse && handleCourseTagToggle(index)}
                    className="cart-item-name"
                    role="button"
                    tabIndex={0}
                    aria-label={`${displayName}${enableDoubleClickCourse ? ' - Double click to tag course' : ''}`}
                  >
                    {ci.course_tag && (
                      <span className={`course-tag course-tag--${ci.course_tag.toLowerCase()}`}>
                        {ci.course_tag}
                      </span>
                    )}
                    <span className="item-name-text">
                      {ci.is_granel && ci.unit_acronym 
                        ? `${displayName} (${ci.unit_acronym})`
                        : displayName
                      }
                    </span>
                    <span className="item-price-inline">
                      ({formatCurrency(displayPrice)})
                    </span>
                  </div>

                  {/* Selection Groups and Modifiers Display */}
                  {((Array.isArray(ci.selection_groups) && ci.selection_groups.length > 0) || modifierOptions) && (
                    <div className={`cart-item-details ${expandedItems[index] ? 'expanded' : 'collapsed'}`}>
                      {/* Display selection group items with their comments */}
                      {Array.isArray(ci.selection_groups) && ci.selection_groups.length > 0 && (
                        <div className="selection-groups-list">
                          {ci.selection_groups
                            .filter(sg => sg && Array.isArray(sg.selected_items))
                            .flatMap(sg => 
                              sg.selected_items
                                .filter(sgi => sgi && sgi.item_id)
                                .map((sgi, sgiIndex) => (
                                  <div key={`${sg.selection_group_id}-${sgi.item_id}-${sgiIndex}`} className="selection-group-item-row">
                                    <span className="selection-item-name">
                                      {sgi.course_tag && (
                                        <span className={`course-tag course-tag--${sgi.course_tag.toLowerCase()}`}>
                                          {sgi.course_tag}
                                        </span>
                                      )}
                                      {(sgi.quantity || 0) > 1 && `${sgi.quantity}x `}{sgi.item_name || 'Item'}
                                    </span>
                                    {sgi.comment && (
                                      <span className="selection-item-comment">
                                        <span className="comment-icon-inline">💬</span> {sgi.comment}
                                      </span>
                                    )}
                                  </div>
                                ))
                            )}
                        </div>
                      )}
                      {/* Display modifiers */}
                      {modifierOptions && (
                        <div className="modifiers-list">
                          {modifierOptions}
                        </div>
                      )}
                      {/* Expand/Collapse button */}
                      {!expandedItems[index] && (ci.selection_groups.length > 3 || (modifierOptions && modifierOptions.length > 100)) && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleExpanded(index)}
                          aria-label="Show more"
                        >
                          ... ver más
                        </button>
                      )}
                      {expandedItems[index] && (
                        <button 
                          className="expand-button"
                          onClick={() => toggleExpanded(index)}
                          aria-label="Show less"
                        >
                          ver menos
                        </button>
                      )}
                    </div>
                  )}

                  {/* Current Comment Display */}
                  {!isEditingComment && ci.comment && (
                    <div className="cart-item-comment">
                      <span className="comment-icon-inline">💬</span> {ci.comment}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="cart-item-actions">
                  <button
                    className="action-button modify-button"
                    onClick={() => loadCartItemForEditing(index)}
                    aria-label="Modify item"
                    title="Modificar artículo"
                  >
                    <HiOutlinePencilSquare />
                  </button>
                  <button
                    className="action-button comment-button"
                    onClick={() => startEditingComment(index, ci.comment || '')}
                    aria-label={ci.comment ? "Edit comment" : "Add comment"}
                    title={ci.comment ? "Editar comentario" : "Agregar comentario"}
                  >
                    <HiOutlineChatBubbleOvalLeft className={ci.comment ? "has-comment" : ""} />
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => {
                      const confirmDelete = window.confirm('¿Está seguro de que desea eliminar este artículo del carrito?');
                      if (confirmDelete) {
                        handleDeleteCartItem(index);
                      }
                    }}
                    aria-label="Delete Item"
                    title="Eliminar artículo"
                  >
                    <HiOutlineTrash />
                  </button>
                </div>
              </div>

              {/* Inline Comment Editor */}
              {isEditingComment && (
                <div className="comment-input-container" ref={el => (commentContainerRefs.current[index] = el)}>
                  <input
                    ref={el => commentInputRefs.current[index] = el}
                    type="text"
                    className="comment-input"
                    value={currentComment}
                    onChange={(e) => setCommentValues(prev => ({ ...prev, [index]: e.target.value }))}
                    onKeyDown={(e) => handleCommentKeyPress(index, e)}
                    placeholder="Agregar comentario..."
                    maxLength={150}
                    aria-label="Edit comment"
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => {
                      // Esperar al siguiente tick para que document.activeElement refleje
                      // correctamente el elemento que recibió el foco tras el blur.
                      setTimeout(() => {
                        const container = commentContainerRefs.current[index];
                        const active = document.activeElement as Node | null;
                        if (container && active && container.contains(active)) {
                          // Si el foco quedó en un elemento dentro del editor (p. ej. botones), no guardamos.
                          return;
                        }
                        // Guardar como si se presionara el check
                        saveComment(index);
                      }, 0);
                    }}
                  />
                  <div className="comment-actions" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="none"
                      className="comment-action-btn cancel-btn"
                      onClick={() => cancelEditingComment(index)}
                      aria-label="Cancel comment edit"
                    >
                      <FaXmark />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </ul>

      {/* Total Cart Value */}
      <div className="cart-total" aria-label={`Cart total: ${formatCurrency(cartTotal)}`}>
        <span className='cart-total-label'>
          Total:
        </span>
        <span className='cart-total-value'>
          {formatCurrency(cartTotal)}
        </span>
      </div>

      {/* Client Selector Portal Dropdown */}
      {activeClientDropdown && clientDropdownPosition && createPortal(
        <div 
          className="portal-client-dropdown-menu"
          style={{
            position: 'fixed',
            top: `${clientDropdownPosition.top}px`,
            left: `${clientDropdownPosition.left}px`,
            width: `${clientDropdownPosition.width}px`,
            zIndex: 99999999,
            background: 'var(--card-color)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
            padding: '0.5rem 0',
            maxHeight: '200px',
            overflowY: 'auto',
            minWidth: '80px'
          }}
        >
          {/* Grupo option */}
          <button
            className={`dropdown-item ${cart[parseInt(activeClientDropdown.split('-')[1])]?.clientIndex === -1 ? 'active' : ''}`}
            onClick={() => handleSelectClientForItem(parseInt(activeClientDropdown.split('-')[1]), -1)}
          >
            Grupo
          </button>
          
          {/* Client options - show up to 20 clients */}
          {Array.from({ length: 20 }, (_, i) => i).map((clientIdx) => (
            <button
              key={clientIdx}
              className={`dropdown-item ${cart[parseInt(activeClientDropdown.split('-')[1])]?.clientIndex === clientIdx ? 'active' : ''}`}
              onClick={() => handleSelectClientForItem(parseInt(activeClientDropdown.split('-')[1]), clientIdx)}
            >
              Cliente {clientIdx + 1}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Cart;
